import api from './api'

export interface LoginCredentials {
  email: string
  password: string
  remember_me?: boolean
}

export interface User {
  user_id: number
  email: string
  user_type: number
  user_type_label: string
  is_owner?: boolean
  is_admin2?: boolean
  can_manage_business_documents?: boolean
  has_business_document_access?: boolean
  user_status: number
  user_status_label: string
  email_verified_at: string | null
  password_updated_at: string | null
  password_changed_time_ago: string
  created_at: string
  updated_at: string
  total_documents?: number
  user_details: {
    user_image: string
    first_name: string
    middle_name?: string
    last_name: string
    street_address?: string
    city?: string
    state?: string
    zip_code?: string
    phone_number?: string
    position?: string
    department?: string
    hire_date?: string
    date_of_birth?: string
    gender?: string
    ssn_last_four?: string
    emergency_contact_name?: string
    emergency_contact_phone?: string
    emergency_contact_relation?: string
    full_name: string
    registered_at: string
  }
}

export interface LoginResponse {
  success: boolean
  message: string
  data: {
    user: User
    token: string
    token_type?: string
    remember_me?: boolean
  }
}

export interface ApiErrorResponse {
  success: boolean
  message: string
  errors?: Record<string, string[]>
}

export interface UpdateProfilePayload {
  first_name?: string
  middle_name?: string | null
  last_name?: string
  street_address?: string | null
  city?: string | null
  state?: string | null
  zip_code?: string | null
  phone_number?: string | null
  date_of_birth?: string | null
  gender?: string | null
  emergency_contact_name?: string | null
  emergency_contact_phone?: string | null
  emergency_contact_relation?: string | null
  user_image?: string | null
  email?: string
  password?: string
  password_confirmation?: string
  current_password?: string
}

export interface MonthAssignment {
  assigned_year: number
  assigned_months: number[]
}

export interface RegisterEmployeePayload {
  email: string
  password: string
  password_confirmation: string
  user_type?: number
  user_status?: number
  first_name: string
  middle_name?: string
  last_name: string
  city?: string
  state?: string
  zip_code?: string
  phone_number?: string
  work_state?: string
  service_population?: string
  user_image?: string
  month_assignments?: MonthAssignment[]
}

export class AuthService {
  static async login(credentials: LoginCredentials): Promise<LoginResponse> {
    try {
      const response = await api.post<LoginResponse>('/auth/login', credentials)
      return response.data
    } catch (error: any) {
      if (error.response?.data) {
        throw error.response.data
      }
      throw {
        success: false,
        message: 'Network error. Please check your connection and try again.'
      }
    }
  }

  static async logout(): Promise<void> {
    try {
      await api.post('/auth/logout')
    } catch (error: any) {
      // Even if logout fails on server, we should clear local storage
      console.warn('Logout API call failed:', error)
    }
  }

  static async logoutFromAllDevices(): Promise<void> {
    try {
      await api.post('/auth/logout-all')
    } catch (error: any) {
      console.warn('Logout from all devices API call failed:', error)
    }
  }

  static async getProfile(): Promise<{ success: boolean; data: { user: User } }> {
    try {
      const response = await api.get('/auth/profile')
      return response.data
    } catch (error: any) {
      if (error.response?.data) {
        throw error.response.data
      }
      throw {
        success: false,
        message: 'Failed to fetch profile. Please try again.'
      }
    }
  }

  static async updateProfile(payload: UpdateProfilePayload): Promise<{ success: boolean; message: string; data: { user: User } }> {
    try {
      const response = await api.put('/auth/profile', payload)
      return response.data
    } catch (error: any) {
      if (error.response?.data) {
        throw error.response.data
      }
      throw {
        success: false,
        message: 'Failed to update profile. Please try again.'
      }
    }
  }

  static async uploadProfileImage(formData: FormData): Promise<{ success: boolean; message: string; data: { image_url: string } }> {
    try {
      const response = await api.post('/auth/profile/upload-image', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })
      return response.data
    } catch (error: any) {
      if (error.response?.data) {
        throw error.response.data
      }
      throw {
        success: false,
        message: 'Failed to upload profile image. Please try again.'
      }
    }
  }

  static async uploadProfileImageBase64(imageData: string): Promise<{ success: boolean; message: string; data: { image_url: string } }> {
    try {
      const response = await api.post('/auth/profile/upload-image-base64', { image_data: imageData })
      return response.data
    } catch (error: any) {
      if (error.response?.data) {
        throw error.response.data
      }
      throw {
        success: false,
        message: 'Failed to upload profile image. Please try again.'
      }
    }
  }

  static async checkAuth(): Promise<boolean> {
    try {
      const token = localStorage.getItem('token')
      if (!token) return false

      await this.getProfile()
      return true
    } catch (error: any) {
      // Only clear token if server explicitly returned 401 Unauthorized or 403 Forbidden
      const status = error?.status || error?.response?.status
      if (status === 401 || status === 403) {
        localStorage.removeItem('token')
      }
      return false
    }
  }

  static async registerEmployee(payload: RegisterEmployeePayload): Promise<{ success: boolean; message: string; data: { user: User; is_reactivation?: boolean } }> {
    try {
      const response = await api.post('/auth/register', payload)
      return response.data
    } catch (error: any) {
      if (error.response?.data) {
        throw error.response.data
      }
      throw {
        success: false,
        message: 'Failed to register employee. Please try again.'
      }
    }
  }

  static async forgotPassword(email: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await api.post('/auth/forgot-password', { email })
      return response.data
    } catch (error: any) {
      if (error.response?.data) {
        throw error.response.data
      }
      throw {
        success: false,
        message: 'Failed to send reset link. Please try again.'
      }
    }
  }

  static async resetPassword(token: string, email: string, password: string, passwordConfirmation: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await api.post('/auth/reset-password', {
        token,
        email,
        password,
        password_confirmation: passwordConfirmation
      })
      return response.data
    } catch (error: any) {
      if (error.response?.data) {
        throw error.response.data
      }
      throw {
        success: false,
        message: 'Failed to reset password. Please try again.'
      }
    }
  }
}

export default AuthService
